// T053: Create PostCard component with T057: image rendering
// T083: Add post options dropdown menu

import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { Post } from '@/types/post'
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { EditPostDialog } from '@/components/post/EditPostDialog'
import { DeletePostDialog } from '@/components/post/DeletePostDialog'
import { formatRelativeTime, formatNumber, formatReputation } from '@/utils/formatters'
import { getProxiedImageUrl } from '@/utils/imageProxy'
import { useAuthStore } from '@/stores/authStore'
import { MoreVertical, Edit, Trash2, Repeat2 } from 'lucide-react'
import { useResteem, useHasResteemed, useCanResteem } from '@/hooks/useResteem'
import { toast } from 'sonner'

interface PostCardProps {
  post: Post
}

export function PostCard({ post }: PostCardProps) {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const isOwnPost = user?.username === post.author
  const postUrl = `/@${post.author}/${post.permlink}`

  // Resteem hooks
  const { data: hasResteemed, isLoading: isCheckingResteem } = useHasResteemed({
    author: post.author,
    permlink: post.permlink,
  })
  const { canResteem } = useCanResteem({ author: post.author })
  const resteemMutation = useResteem()

  const handleResteem = (e: React.MouseEvent) => {
    e.stopPropagation()

    if (!user) {
      toast.error('Please login to resteem posts')
      return
    }

    if (hasResteemed) {
      toast.info('You have already resteemed this post')
      return
    }

    resteemMutation.mutate(
      { author: post.author, permlink: post.permlink },
      {
        onSuccess: () => {
          toast.success('Post has been shared to your blog')
        },
        onError: (error) => {
          toast.error('Resteem failed', {
            description: error.message,
          })
        },
      }
    )
  }

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on interactive elements
    const target = e.target as HTMLElement
    if (target.closest('a, button, [role="button"]')) {
      return
    }
    navigate(postUrl)
  }

  return (
    <Card className="hover:bg-accent/50 transition-colors cursor-pointer" onClick={handleCardClick}>
      <CardHeader className="flex flex-row items-start gap-4 space-y-0">
        <Avatar>
          <AvatarImage
            src={`https://steemitimages.com/u/${post.author}/avatar`}
            alt={post.author}
          />
          <AvatarFallback>{post.author[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-1 space-y-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link to={`/@${post.author}`} className="font-semibold hover:underline">{post.author}</Link>
              <span className="text-sm text-muted-foreground">
                ({formatReputation(post.authorReputation)})
              </span>
              <span className="text-sm text-muted-foreground">·</span>
              <span className="text-sm text-muted-foreground">
                {formatRelativeTime(post.created)}
              </span>
            </div>
            {isOwnPost && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setEditDialogOpen(true)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => setDeleteDialogOpen(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        {post.title && (
          <h3 className="text-xl font-semibold line-clamp-2">{post.title}</h3>
        )}

        {post.summary && (
          <p className="text-muted-foreground line-clamp-3">{post.summary}</p>
        )}

        {post.images && post.images.length > 0 && post.images[0] && (
          <img
            src={getProxiedImageUrl(post.images[0])}
            alt=""
            className="w-full rounded-md object-cover max-h-96 bg-muted"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        )}

        {post.tags && post.tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {post.tags.slice(0, 3).map((tag) => (
              <Link
                key={tag}
                to={`/search?tags=${encodeURIComponent(tag)}`}
                className="text-xs bg-secondary text-secondary-foreground px-2 py-1 rounded-md hover:bg-secondary/80 transition-colors"
              >
                #{tag}
              </Link>
            ))}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex flex-wrap gap-3 sm:gap-6 text-sm text-muted-foreground">
        <div className="flex items-center gap-1">
          <span>💬</span>
          <span>{formatNumber(post.commentCount)}</span>
        </div>
        <div className="flex items-center gap-1">
          <span>⬆</span>
          <span>{formatNumber(post.upvoteCount)}</span>
        </div>
        {/* Resteem button */}
        {!isOwnPost && (
          <button
            onClick={handleResteem}
            disabled={resteemMutation.isPending || isCheckingResteem || hasResteemed || !canResteem}
            className={`flex items-center gap-1 transition-colors ${
              hasResteemed
                ? 'text-green-500'
                : canResteem
                  ? 'hover:text-green-500'
                  : 'opacity-50 cursor-not-allowed'
            }`}
            title={hasResteemed ? 'Already resteemed' : canResteem ? 'Resteem' : 'Cannot resteem'}
          >
            <Repeat2 className={`h-4 w-4 ${resteemMutation.isPending ? 'animate-spin' : ''}`} />
            <span>{hasResteemed ? 'Resteemed' : 'Resteem'}</span>
          </button>
        )}
        {post.payout > 0 && (
          <div className="flex items-center gap-1">
            <span>💰</span>
            <span>${post.payout.toFixed(2)}</span>
          </div>
        )}
      </CardFooter>

      {/* Edit/Delete Dialogs */}
      {isOwnPost && (
        <>
          <EditPostDialog
            open={editDialogOpen}
            onOpenChange={setEditDialogOpen}
            post={post}
          />
          <DeletePostDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
            post={post}
          />
        </>
      )}
    </Card>
  )
}
